import type { SyntheticEvent } from 'react'

/**
 * Per-field clear (×) on a CLOSED search-bar pill — Searchbar Pro parity
 * (#870 QA, Eli 2026-08-05). Markup mirrors the toolkit's
 * `search-bar.php:429-439` (one compound-path SVG, stroke-width 2, round caps)
 * and the handlers mirror `searchbar-pro.js:3536-3557`.
 *
 * A <span role="button">, not a <button>: for When / Who / Filters the pill
 * ITSELF is the Radix DialogTrigger <button>, and a nested <button> is invalid
 * HTML. Where's field is a plain <div>, but it uses the same element so all
 * four fields behave identically.
 *
 * Propagation, both halves of it:
 *  - onClick stops SYNTHETIC propagation. Radix's DialogTrigger binds
 *    `onOpenToggle` to onClick (react-dialog 1.1.15,
 *    dist/index.mjs:74) — same React tree, so this is what actually keeps a
 *    clear from opening the popover.
 *  - onPointerDown / onMouseDown stop AND preventDefault so the press never
 *    moves focus onto the parent field (which, for Where, would reopen the
 *    listbox on the way out). This is the toolkit's mousedown guard, kept for
 *    both event families because a touch device fires only the pointer one.
 *
 * Visibility is VALUE-driven, never hover-driven: callers mount this only
 * while the field has an applied value.
 */
export default function FieldClear({ label, onClear }: { label: string; onClear: () => void }) {
  const fire = (e: SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onClear()
  }
  const swallow = (e: SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }
  return (
    <span
      className="hfse-sbp-field-clear"
      role="button"
      // tabindex="-1", matching the toolkit (search-bar.php:431): the span
      // sits inside the field's own <button>, and a focusable descendant
      // there violates the button content model, puts a Tab stop on every
      // filled pill, and folds "Clear dates" into the parent trigger's
      // accessible name (2026-08-05 audit). Keyboard users clear from inside
      // the popover; the pointer path keeps this control.
      tabIndex={-1}
      aria-label={label}
      onPointerDown={swallow}
      onMouseDown={swallow}
      onClick={fire}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') fire(e)
      }}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </span>
  )
}
